import { useState } from "react";
import { Select, selectState as ss } from "@bfc-software/pallet";
import type { SelectMsg, SelectState } from "@bfc-software/pallet";
import { match } from "ts-pattern";
import { useCurrentLocalization } from "@bfc-software/bfc-web-ui/i18n";
import {
  fromQuery,
  mapAsync,
  type Async,
} from "@bfc-software/bfc-web-ui/lib/async-utils";
import * as selectUtils from "@bfc-software/bfc-web-ui/lib/select-utils";

import { selectorParam, useTaskFilters } from "../-data/filters";
import { matchesSelectorQuery } from "../-data/selector-options";
import {
  useSelectorRoster,
  type SelectorOption,
} from "../-data/use-selector-roster";

/**
 * The selector filter, shared by both tabs.
 *
 * It sits in the tab bar rather than inside either tab, for the same reason
 * the two search boxes sit one level up in `_layout.tsx`: `selector` is a URL
 * param both tabs read through `useTaskFilters`, and a control that lived
 * inside one tab would stop narrowing the moment the other tab was open.
 *
 * Every option is a person, and picking one reaches the wire on both tabs as
 * the same `selectorAppUserExtIds` param: the activation listing narrows to
 * that person's rows, and the plan listing to the plans holding a live
 * assignment to them. The box used to carry an "unassigned" option as well,
 * which no plan endpoint could answer for - the tasks nobody holds are a task
 * status, and the All tasks tab's `Unassigned` chip is where that question is
 * asked now.
 *
 * The typed text is held here in `useState` rather than in the URL: it is not a
 * filter in its own right, only the means of picking one, so it is not
 * something a manager would want to send to someone else. Pallet owns whether
 * the dropdown is open and reports it; this filter clears its text on `closed`.
 * What reaches the URL is the picked option's id, exactly as `filters.ts`
 * expects it back - and the roster is what turns that id back into a name,
 * which is why it is read on mount rather than on first open.
 *
 * A failed roster load is a disabled field with a reason, not an empty list.
 */

/** Pure: everything the dropdown shows. The roster is narrowed here; pallet never filters. */
function selectorFilterState(a: {
  roster: Async<ReadonlyArray<SelectorOption>>;
  selectedId: string | null;
  displayValue: string;
  text: string;
  t: (key: string) => string;
}): SelectState<SelectorOption> {
  const label = ss.ariaLabel(a.t("tasks.selectorFilter.ariaLabel"));
  return selectUtils.fromAsync(
    mapAsync(a.roster, (all) =>
      all.filter((option) => matchesSelectorQuery(option, a.text)),
    ),
    {
      label,
      loading: a.t("tasks.selectorFilter.loading"),
      empty: a.t("tasks.selectorFilter.empty"),
      failed: a.t("tasks.selectorFilter.failed"),
    },
    (options) => {
      const base = ss.single<SelectorOption>({
        label,
        placeholder: a.t("tasks.selectorFilter.placeholder"),
        options,
      });
      const picked =
        a.selectedId === null
          ? base
          : ss.withPick(base, { id: a.selectedId, label: a.displayValue });
      return ss.withSearch(picked, {
        placeholder: a.t("tasks.selectorFilter.searchPlaceholder"),
        text: a.text,
      });
    },
  );
}

export function SelectorFilter() {
  const [t] = useCurrentLocalization();

  const { filters, setSearch } = useTaskFilters();

  const [text, setText] = useState("");

  const roster = useSelectorRoster();
  const rosterOptions = roster.data ?? [];

  /**
   * What the box shows when it is not the thing someone is mid-keystroke on:
   * read off the ADT rather than off the raw `selector` param, so a `named`
   * filter shows the roster's own label rather than the bare AAA id. The
   * roster is read on mount, so that is a first-paint flicker at worst rather
   * than the id standing until somebody opens the box - and the id is what the
   * task table's own Selector column draws anyway, so even the flicker does not
   * look unresolved.
   */
  const displayValue = ((): string => {
    switch (filters.selector.kind) {
      case "any":
        return "";
      case "named": {
        // Read into a local first: narrowing does not survive into the
        // `find` callback below.
        const name = filters.selector.name;
        const known = rosterOptions.find((option) => option.id === name);
        return known?.label ?? name;
      }
    }
  })();

  const selectedId = selectorParam(filters.selector) ?? null;

  const onMsg = (msg: SelectMsg<SelectorOption>) =>
    match(msg)
      .with({ kind: "searched" }, ({ text }) => setText(text))
      // Reset the search box, so reopening shows the full list.
      .with({ kind: "closed" }, () => setText(""))
      .with({ kind: "picked" }, ({ item }) => setSearch({ selector: item.id }))
      .with({ kind: "cleared" }, () => setSearch({ selector: undefined }))
      // `pickedN` cannot arrive from `single`
      .with({ kind: "pickedN" }, { kind: "opened" }, () => {})
      .exhaustive();

  return (
    <Select
      state={selectorFilterState({
        roster: fromQuery(roster),
        selectedId,
        displayValue,
        text,
        t,
      })}
      onMsg={onMsg}
      className="flex-1 min-w-0 ml-16"
      data-testid="selector-filter"
    />
  );
}
